import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Autocomplete,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "./Layout";
import { superlogin, verifyAdminMfa } from "../actions/authenActions";

const Admin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    user_id: "",
    password: "",
    org_email: "",
  });
  const [otpCode, setOtpCode] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [submissionError, setSubmissionError] = useState(null);

  const loggedInUser = useSelector((state) => state.authen.loggedInUser);
  const loggedInAdmin = useSelector((state) => state.authen.loggedInAdmin);
  const accessToken = useSelector((state) => state.authen.accessToken);
  const mfaPendingAdmin = useSelector((state) => state.authen.mfaPendingAdmin);

  const organizationOptions = useMemo(() => {
    if (!loggedInAdmin || !loggedInAdmin.data) return [];
    return [...loggedInAdmin.data]
      .sort((a, b) =>
        (a.organization_name || "").localeCompare(b.organization_name || ""),
      )
      .map((item) => ({ label: item.organization_name, email: item.email }));
  }, [loggedInAdmin]);

  useEffect(() => {
    if (loggedInUser) {
      if (loggedInUser.status === "Pass") {
        if (!organizationOptions.length) navigate("/dashboard");
      } else {
        setSubmissionError(loggedInUser.message || "Login failed");
      }
    }
  }, [loggedInUser, organizationOptions, navigate]);

  const awaitingMfa =
    !!mfaPendingAdmin?.mfa_token && !organizationOptions.length;
  const shouldShowOrgSelect = organizationOptions.length > 0;

  function validateCredentials() {
    const errors = {};
    if (!formData.user_id) errors.user_id = "Username is required";
    if (!formData.password) errors.password = "Password is required";
    return errors;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
    setSubmissionError(null);
  }

  function handleCredentialsSubmit(e) {
    e.preventDefault();
    const errors = validateCredentials();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    dispatch(
      superlogin({ user_id: formData.user_id, password: formData.password }),
    ).catch((error) =>
      setSubmissionError(error?.message || "An unexpected error occurred"),
    );
  }

  function handleOtpSubmit(e) {
    e.preventDefault();
    if (!otpCode) {
      setFormErrors({ otp: "Enter the 6-digit code" });
      return;
    }
    dispatch(
      verifyAdminMfa({
        mfa_token: mfaPendingAdmin.mfa_token,
        otp_token: otpCode,
      }),
    ).catch((error) =>
      setSubmissionError(error?.message || "Invalid or expired code"),
    );
  }

  function handleOrgSubmit(e) {
    e.preventDefault();
    if (!formData.org_email) {
      setFormErrors({ org_email: "Please select an organization" });
      return;
    }
    const payload = {
      user_id: formData.user_id,
      password: formData.password,
      email: formData.org_email,
      ...(accessToken ? { accessToken } : {}),
    };
    dispatch(superlogin(payload))
      .then(() => navigate("/dashboard"))
      .catch((error) =>
        setSubmissionError(error?.message || "An unexpected error occurred"),
      );
  }

  return (
    <Layout>
      <Typography variant="h5" gutterBottom>
        Admin Login
      </Typography>

      {submissionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submissionError}
        </Alert>
      )}

      {!awaitingMfa && !shouldShowOrgSelect && (
        <Box
          component="form"
          onSubmit={handleCredentialsSubmit}
          noValidate
          autoComplete="off"
          sx={{ mt: 2 }}
        >
          <TextField
            label="Username"
            name="user_id"
            fullWidth
            margin="normal"
            value={formData.user_id}
            onChange={handleChange}
            error={!!formErrors.user_id}
            helperText={formErrors.user_id}
          />
          <TextField
            label="Password"
            type="password"
            name="password"
            fullWidth
            margin="normal"
            value={formData.password}
            onChange={handleChange}
            error={!!formErrors.password}
            helperText={formErrors.password}
          />
          <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }}>
            Login
          </Button>
        </Box>
      )}

      {awaitingMfa && (
        <Box
          component="form"
          onSubmit={handleOtpSubmit}
          noValidate
          sx={{ mt: 2 }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            Enter the code from Microsoft Authenticator
          </Typography>
          <TextField
            label="Authentication code"
            name="otp"
            fullWidth
            margin="normal"
            value={otpCode}
            onChange={(e) => {
              setOtpCode(e.target.value);
              setFormErrors({});
              setSubmissionError(null);
            }}
            error={!!formErrors.otp}
            helperText={formErrors.otp}
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
          />
          <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }}>
            Verify
          </Button>
        </Box>
      )}

      {shouldShowOrgSelect && (
        <Box
          component="form"
          onSubmit={handleOrgSubmit}
          noValidate
          sx={{ mt: 2 }}
        >
          <Autocomplete
            options={organizationOptions}
            getOptionLabel={(option) => option.label || ""}
            filterOptions={(options, state) => {
              const input = (state.inputValue || "").toLowerCase();
              return options.filter((option) =>
                (option.label || "").toLowerCase().includes(input),
              );
            }}
            value={
              organizationOptions.find(
                (org) => org.email === formData.org_email,
              ) || null
            }
            onChange={(event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                org_email: newValue ? newValue.email : "",
              }));
              setFormErrors((prev) => ({ ...prev, org_email: "" }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Organization"
                margin="normal"
                error={!!formErrors.org_email}
                helperText={formErrors.org_email}
              />
            )}
            fullWidth
          />
          <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }}>
            Continue
          </Button>
        </Box>
      )}
    </Layout>
  );
};

export default Admin;
